import { computed, reactive, readonly, ref, shallowRef } from "vue";
import type { ClientDto, DashboardMessage, LogEvent, ScreenTimeEntry } from "@/types/protocol";

/**
 * Zentraler Zustand des Dashboards: haelt die WebSocket-Verbindung zu
 * <c>/dashboard-ws</c> offen und pflegt Clients, Bildschirmzeiten und das
 * Ereignis-Log. Bewusst ein Modul-Singleton (kein Store-Framework) - es gibt
 * genau eine Datenquelle und genau einen Verbraucher.
 */

/** So viele Ereignisse haelt die Oberflaeche vor (der Server puffert 500). */
const EVENT_LIMIT = 500;

const RECONNECT_DELAY_MS = 3000;

export type ConnectionState = "connecting" | "open" | "closed";

const clients = shallowRef<ClientDto[]>([]);
const screenTime = shallowRef<ScreenTimeEntry[]>([]);
const events = shallowRef<LogEvent[]>([]);
const connection = ref<ConnectionState>("connecting");
const lastUpdate = ref<Date | null>(null);

let socket: WebSocket | null = null;
let reconnectTimer: number | undefined;

function socketUrl(): string {
  const protocol = location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${location.host}/dashboard-ws`;
}

function apply(message: DashboardMessage): void {
  switch (message.type) {
    case "snapshot":
      clients.value = message.clients ?? [];
      screenTime.value = message.screenTime ?? [];
      events.value = message.events ?? []; // bereits neueste zuerst
      break;
    case "clients":
      clients.value = message.clients ?? [];
      break;
    case "screenTime":
      screenTime.value = message.screenTime ?? [];
      break;
    case "event":
      events.value = [message.event, ...events.value].slice(0, EVENT_LIMIT);
      break;
  }
  lastUpdate.value = new Date();
}

function connect(): void {
  window.clearTimeout(reconnectTimer);
  connection.value = "connecting";

  const ws = new WebSocket(socketUrl());
  socket = ws;

  ws.onopen = () => {
    connection.value = "open";
  };

  ws.onmessage = (event) => {
    try {
      apply(JSON.parse(event.data as string) as DashboardMessage);
    } catch {
      // Kaputte Nachricht ignorieren - die naechste bringt den Zustand ohnehin nach.
    }
  };

  ws.onerror = () => ws.close();

  ws.onclose = () => {
    if (socket === ws) {
      socket = null;
      connection.value = "closed";
      reconnectTimer = window.setTimeout(connect, RECONNECT_DELAY_MS);
    }
  };
}

/** Startet die Verbindung (idempotent). */
export function startDashboard(): void {
  if (!socket) {
    connect();
  }
}

/** Beendet die Verbindung und verhindert weitere Reconnects. */
export function stopDashboard(): void {
  window.clearTimeout(reconnectTimer);
  const ws = socket;
  socket = null;
  ws?.close();
  connection.value = "closed";
}

export const dashboard = readonly(
  reactive({
    clients,
    screenTime,
    events,
    connection,
    lastUpdate,
    clientCount: computed(() => clients.value.length),
    activeUsers: computed(() => screenTime.value.filter((entry) => entry.activeNow).length),
  }),
);
